import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import { Star } from 'lucide-react';
import OnboardingProgress from '../../components/OnboardingProgress';

const experienceLevels = [
  {
    value: 'beginner',
    label: 'Beginner/Starting Out',
    description: 'New to the industry and eager to learn',
  },
  {
    value: 'intermediate',
    label: 'Intermediate',
    description: '2-5 years of experience',
  },
  {
    value: 'professional',
    label: 'Professional',
    description: '5+ years of industry experience',
  },
  {
    value: 'veteran',
    label: 'Industry Veteran',
    description: '10+ years of established experience',
  },
] as const;

const schema = z.object({
  experienceLevel: z.enum(['beginner', 'intermediate', 'professional', 'veteran']),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingExperience() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      experienceLevel: user?.experience_level || 'beginner',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await updateProfile({
        experience_level: data.experienceLevel,
      });
      navigate('/onboarding/goals');
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="experience" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl text-center font-bold font-dillan text-primary mb-2">
          Tell us your experience level
        </h1>
        <p className="text-neutral text-center text-l mb-6">
          Being honest about your experience helps set the right expectations for collaborations.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {experienceLevels.map((level) => (
              <label
                key={level.value}
                className="flex items-start rounded-full bg-surface-light cursor-pointer transition-colors"
              >
                <input
                  type="radio"
                  value={level.value}
                  {...register('experienceLevel')}
                  className="hidden peer"
                />
          
                {/* ✅ Wrap text content inside a div that gets border update */}
                <div className="w-full border-4 border-neutral rounded-full p-2 pl-8 peer-checked:border-secondary">
                  <div className="text-neutral font-bold">{level.label}</div>
                  <div className="text-sm text-neutral">{level.description}</div>
                </div>
              </label>
            ))}
          </div>


          {errors.experienceLevel && (
            <p className="mt-1 text-sm text-primary">{errors.experienceLevel.message}</p>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-8"
              onClick={() => navigate('/onboarding/location')}
            >
              Back
            </Button>
            <Button type="submit" variant="primary" className="flex-1 text-surface-light mt-8">
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}